import React, { useState, useEffect, useMemo } from "react";
import styles from "./index.module.css";
import clsx from "clsx";
import { TIMELINE_STEPS } from "../../utils/constant";
import Timeline from "./Timeline";
import { getTrancheBotV2LogsData } from "../../service/api";
import { showNotification } from "../../service/showNotification";
import { processTimelineData } from "../../utils/timelineUtils";
import { Card, Table } from "react-bootstrap";

const DetailRow = ({ label, value }) => (
  <div className={styles.detailRow}>
    <span className={styles.detailLabel}>{label}</span>
    <span>{value ?? "-"}</span>
  </div>
);

const SkeletonRows = ({ count = 5, width = "80%" }) => (
  <>
    {[...Array(count)].map((_, i) => (
      <div
        key={i}
        className={clsx(styles.skeleton, styles.skeletonText)}
        style={{ width }}
      />
    ))}
  </>
);

const TrancheOpportunityCardSkeleton = () => (
  <div className={styles.card}>
    <div className={styles.timeline}>
      {[...Array(4)].map((_, i) => (
        <div key={i} className={styles.timelineStep}>
          <div className={clsx(styles.timelineDot, styles.skeleton)} />
          <div
            className={clsx(styles.skeleton, styles.skeletonText)}
            style={{ width: "50px" }}
          />
        </div>
      ))}
    </div>
    <div className={styles.cardContent}>
      <div className={styles.opportunitySection}>
        <div className={clsx(styles.skeleton, styles.skeletonTitle)} />
        <SkeletonRows />
      </div>
      <div className={styles.trancheSection}>
        <div className={clsx(styles.skeleton, styles.skeletonTitle)} />
        <SkeletonRows width="70%" />
      </div>
    </div>
  </div>
);

// Main Component
const TrancheOpportunityCard = ({ trancheId }) => {
  const [loading, setLoading] = useState(true);
  const [tranche, setTranche] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!trancheId) {
      setError("Invalid tranche ID.");
      setLoading(false);
      return;
    }

    const fetchTrancheDetails = async () => {
      try {
        setLoading(true);
        const response = await getTrancheBotV2LogsData(trancheId);
        if (!response?.data?.data) throw new Error("No tranche data returned");
        setTranche(response.data.data);
      } catch (err) {
        console.error(err);
        setError("Failed to load tranche details.");
        showNotification("error", "Failed to load tranche details.");
      } finally {
        setLoading(false);
      }
    };

    fetchTrancheDetails();
  }, [trancheId]);

  const timelineData = useMemo(
    () => processTimelineData(tranche?.trancheLogs),
    [tranche?.trancheLogs]
  );

  if (loading) return <TrancheOpportunityCardSkeleton />;
  if (error) return <div className={styles.error}>{error}</div>;
  if (!tranche)
    return <div className={styles.error}>No tranche data available.</div>;

  const activeIndex = timelineData.findIndex((step) => step.isActive);
  const progressWidth =
    activeIndex > 0
      ? (activeIndex /
          (TIMELINE_STEPS.length - (activeIndex === 1 ? 1.4 : 1))) *
        100
      : 0;

  const opportunityFields = [
    { label: "Opportunity ID", value: tranche?.opportunityId },
    {
      label: "Name",
      value:
        tranche.opportunityDetails?.opportunityName || tranche.opportunityName,
    },
    {
      label: "Equity Contribution",
      value: tranche.opportunityDetails?.ecToDeploy
        ? `$${tranche.opportunityDetails.ecToDeploy.toLocaleString()}`
        : "-",
    },
    {
      label: "ITC %",
      value: tranche?.opportunityDetails?.itcPercent
        ? `${tranche.opportunityDetails.itcPercent}%`
        : "-",
    },
    {
      label: "Internal Markup %",
      value: tranche?.opportunityDetails?.internalMarkupPercent,
    },
    {
      label: "Affiliate Markup %",
      value: tranche?.opportunityDetails?.affiliateMarkupPercent,
    },
    { label: "AMP 30%", value: tranche?.opportunityDetails?.ampThirtyPercent },
    { label: "AMP 40%", value: tranche?.opportunityDetails?.ampFortyPercent },
    { label: "States", value: tranche?.opportunityDetails?.state?.join(", ") },
    {
      label: "Contact Group",
      value: tranche?.opportunityDetails?.contactGroup,
    },
  ];

  const trancheFields = [
    { label: "Opportunity ID", value: tranche?.opportunityId },
    { label: "Name", value: tranche.opportunityName },
    {
      label: "Equity Contribution",
      value: tranche.ecToDeploy
        ? `$${tranche.ecToDeploy.toLocaleString()}`
        : "-",
    },
    {
      label: "ITC %",
      value: tranche.itcPercent ? `${tranche.itcPercent}%` : "-",
    },
    { label: "Internal Markup %", value: tranche.internalMarkupPercent },
    { label: "Affiliate Markup %", value: tranche.affiliateMarkupPercent },
    { label: "AMP 30%", value: tranche.ampThirtyPercent },
    { label: "AMP 40%", value: tranche.ampFortyPercent },
    { label: "States", value: tranche.state?.join(", ") },
    { label: "Contact Group", value: tranche.contactGroup },
    {
      label: "Total ITC %",
      value: tranche.totalItcPercent ? `${tranche.totalItcPercent}%` : "-",
    },
    {
      label: "Total Purchase Amount",
      value: tranche.totalPurchaseAmount
        ? `$${tranche.totalPurchaseAmount.toLocaleString()}`
        : "-",
    },
  ];

  return (
    <div className={styles.card}>
      <Timeline timelineData={timelineData} progressWidth={progressWidth} />

      <div className={styles.cardContent}>
        <div className={styles.trancheOpportunitySection}>
          <h2>Opportunity (Initial)</h2>
          {opportunityFields.map((field) => (
            <DetailRow key={field.label} {...field} />
          ))}
        </div>

        <div className={styles.trancheSection}>
          <h2>Tranche (Modified)</h2>
          {trancheFields.map((field) => (
            <DetailRow key={field.label} {...field} />
          ))}
        </div>
      </div>

      {tranche.assetIds && tranche.assetIds.length > 0 && (
        <div className={styles.trancheAssetsSection}>
          <h2 className={styles.trancheSectionTitle}>Assets</h2>
          <ul className={styles.trancheAssetList}>
            {tranche.assetIds.map((assetId) => (
              <li key={assetId} className={styles.trancheAssetItem}>
                {assetId}
              </li>
            ))}
          </ul>
        </div>
      )}

      {tranche.rejectionMessage && (
        <div className={styles.rejectionMessage}>
          <strong>Reason:</strong> {tranche.rejectionMessage}
        </div>
      )}
      <div className="scrollable-table-container2">
        <h2 className={styles.trancheSectionTitle}>Tranche Logs</h2>
        <Card
          border="light"
          className="table-wrapper table-responsive shadow-sm"
        >
          <Card.Body className="pt-0">
            <Table hover className="user-table align-items-center">
              <thead>
                <tr>
                  <th className={styles.detailLogHeadersCenter}>TimeStamp</th>
                  <th
                    className={clsx(
                      styles.detailLogHeadersCenter,
                      styles.statusClass
                    )}
                  >
                    Status
                  </th>
                  <th className={styles.detailLogHeadersCenter}>Message</th>
                </tr>
              </thead>

              <tbody>
                {!tranche?.trancheLogs?.length ? (
                  <tr>
                    <td colSpan={3} className="bg-grey text-center py-3">
                      No Data Found.
                    </td>
                  </tr>
                ) : (
                  tranche.trancheLogs.map((log, index) => {
                    const status = log.status?.toLowerCase();
                    return (
                      <tr key={index}>
                        <td className={styles.detailedLogsValue}>
                          {log.timestamp
                            ? new Date(log.timestamp).toLocaleString()
                            : "-"}
                        </td>
                        <td className={styles.detailedLogsValue}>
                          <span
                            className={clsx(styles.statusCapsule, {
                              [styles.statusSuccess]: [
                                "success",
                                "zoho success",
                              ].includes(status),
                              [styles.statusRed]: status === "failed",
                              [styles.statusYellow]: status === "pending",
                              [styles.statusOrange]: status?.includes("draft"),
                              [styles.statusBlue]:
                                status === "sent for approval",
                              [styles.statusFailure]:
                                ![
                                  "success",
                                  "zoho success",
                                  "pending",
                                  "sent for approval",
                                ].includes(status) &&
                                !status?.includes("draft"),
                            })}
                          >
                            {log.status || "-"}
                          </span>
                        </td>
                        <td className={styles.detailedLogsValue}>
                          {log.message || "-"}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      </div>
    </div>
  );
};

export default TrancheOpportunityCard;
